import axios from 'axios';
import api from './apiConfig';
import { Pup, Reaction } from '../types/breed';

export const getFeaturedPup = async (): Promise<Pup | null> => {
  try {
    const response = await api.get<Pup>('/api/pups/featured');
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getPups = async (breedId?: number, username?: string): Promise<Pup[]> => {
  try {
    const params: Record<string, any> = {};
    if (breedId) params.breed_id = breedId;
    if (username) params.username = username;
    
    const response = await api.get<{ pups: Pup[] }>('/api/pups', { params });
    return response.data.pups;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Failed to fetch pups');
    }
    throw new Error('Network error when trying to fetch pups');
  }
};

export const getPup = async (pupId: number): Promise<Pup> => {
  try {
    const response = await api.get<Pup>(`/api/pups/${pupId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Failed to fetch pup');
    }
    throw new Error('Network error when trying to fetch pup');
  }
};

export const uploadPup = async (
  formData: FormData
): Promise<{ id: number; image_url: string; breed_id: number }> => {
  try {
    const response = await api.post<{ id: number; image_url: string; breed_id: number }>('/api/pups', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Failed to upload pup');
    }
    throw new Error('Network error when trying to upload pup');
  }
};

export const updatePup = async (
  pupId: number,
  pupData: {
    breed_id?: number;
    dog_name?: string;
    caption?: string;
    location_name?: string;
  }
): Promise<Pup> => {
  try {
    const response = await api.put<Pup>(`/api/pups/${pupId}`, pupData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Failed to update pup');
    }
    throw new Error('Network error when trying to update pup');
  }
};

export const deletePup = async (pupId: number): Promise<{success: boolean; message: string}> => {
  try {
    const response = await api.delete<{success: boolean; message: string}>(`/api/pups/${pupId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Failed to delete pup');
    }
    throw new Error('Network error when trying to delete pup');
  }
};

export const reactToPup = async (
  pupId: number, 
  reaction: Reaction['reaction_type']
): Promise<{ success: boolean; action: 'added' | 'removed' }> => {
  try {
    const response = await api.post<{ success: boolean; action: 'added' | 'removed' }>(`/api/pups/${pupId}/react`, {
      reaction_type: reaction
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Failed to react to pup');
    }
    throw new Error('Network error when trying to react to pup');
  }
};